import { apiRequest, apiBase, authHeaders } from '../api';

// Package "apppack": Compile & email application documents on a job (report 54fd6fc9 — server/src/services/jobAppPack.ts,
// GET /jobs/:id/app-pack + POST /jobs/:id/app-pack/send). The rule's Application documents, looked up in Our Documents.
/** `lastPage`: a Company Documents file standing in for a Shareholder List with none on file — only its last page goes
 *  into the PDF (report 0c6a8f8c). */
export interface AppPackFile { nodeId: string; name: string; date: string; folder: string; noEmail: boolean; lastPage?: boolean }
/** `from`: the earliest date this entry's files may carry — the pack's, or a yearly filing's own (a P.N.D. 91, a P.N.D. 50 or an Income Statement from 31 December, a Balance Sheet from 1 January — reports 0c6a8f8c and c7fd8843); '' for an entry of any date (a Company Certificate — report c7fd8843, opening #3). */
/** `filled`: 'yes' when no TM.7 is on file, so a filled-in one goes into the PDF instead (counted as found); else ''. */
/** `copies`: the count is copies of one document (a Passport — report 77e4b1e9): its one file, the newest, answers every copy and goes into the PDF `count` times. */
export interface AppPackRow { key: string; docName: string; count: number; from: string; files: AppPackFile[]; older: AppPackFile | null; filled?: string; copies?: boolean }
export interface AppPackPreview {
  jobNo: string; client: string; company: string; service: string;
  expiry: string; expirySource: 'filed' | 'typed' | 'none'; filedExpiry: string; expiryFile: string;
  /** The earliest date a file may carry ('YYYY-MM-DD'): six months before the expiry. A yearly filing's entry carries its own, earlier one (AppPackRow.from). */
  from: string;
  rows: AppPackRow[]; wanted: number; found: number;
  sender: string; warning: string;
  lastSend: { at: number; byName: string; reason: string } | null;
}
export interface AppPackSent {
  ok: true; sentAt: number; to: string[]; cc: string[]; from: string; fileName: string; size: number; wanted: number; included: number;
  /** `filled`: where the filled-in TM.7 that went in was filled from ('' = none went in for this row).
   *  `lastPage`: the name of the file whose last page alone stood in for the row ('' = none).
   *  `copies`: its one file went in `count` times (AppPackRow.copies). */
  rows: Array<{ docName: string; count: number; included: string[]; unmerged: string[]; noEmail: string[]; missing: number; filled?: '' | AppPackTm7Source; lastPage?: string; copies?: boolean }>;
}

export const appPackApi = {
  preview: (jobId: string, expiry: string): Promise<AppPackPreview> =>
    apiRequest('GET', `/jobs/${encodeURIComponent(jobId)}/app-pack${expiry ? `?expiry=${encodeURIComponent(expiry)}` : ''}`),
  send: (jobId: string, data: { to: string; cc: string; expiry: string }): Promise<AppPackSent> =>
    apiRequest('POST', `/jobs/${encodeURIComponent(jobId)}/app-pack/send`, data),
};

/** Where a filled-in TM.7's answers came from: the passport was read · no passport on file, the client record alone ·
 *  a passport is on file but could not be read, the record alone. */
export type AppPackTm7Source = 'passport' | 'record' | 'unread';
/** The TM.7 filled in from the person's passport and the client record (GET /jobs/:id/app-pack/tm7.pdf —
 *  server/src/services/tm7Form.ts), for the expiry the dialog shows. It opens in `tab`, which the click itself opened so
 *  no pop-up blocker stops it; the answer is where the form's answers came from. */
export async function appPackTm7(jobId: string, expiry: string, tab: Window | null): Promise<AppPackTm7Source | ''> {
  try {
    const res = await fetch(`${apiBase()}/jobs/${encodeURIComponent(jobId)}/app-pack/tm7.pdf${expiry ? `?expiry=${encodeURIComponent(expiry)}` : ''}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as any).error || res.statusText || 'TM.7 failed');
    const source = String(res.headers.get('X-Tm7-Source') || '');
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return source === 'passport' || source === 'record' || source === 'unread' ? source : '';
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}
